import React from 'react';
import { motion } from 'framer-motion';
import {
  Loader2, CheckCircle2, AlertCircle, MapPin, Lock, Shield, Award,
} from 'lucide-react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import WaitlistForm from '../WaitlistForm';

const fade = {
  initial: { opacity: 0, x: 20 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -20 },
  transition: { duration: 0.25 },
};

export const CheckoutStep = ({
  form,
  setField,
  validating,
  isServiceable,
  serviceAreaName,
  showWaitlist,
  setShowWaitlist,
}) => (
  <motion.div
    key="s5"
    {...fade}
    className="bg-white rounded-2xl p-6 shadow-sm border border-zinc-100"
    data-testid="step-checkout"
  >
    <h2 className="text-lg sm:text-xl font-bold text-zinc-900 font-heading mb-1">
      Complete Your Booking
    </h2>
    <p className="text-sm text-zinc-500 mb-5">Just a few details and you&apos;re all set</p>

    <div className="space-y-4">
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label className="text-sm font-semibold text-zinc-700 mb-1.5 block">Full Name</Label>
          <Input
            value={form.fullName}
            onChange={(e) => setField('fullName', e.target.value)}
            placeholder="Jane Smith"
            data-testid="input-name"
            className="h-11"
          />
        </div>
        <div>
          <Label className="text-sm font-semibold text-zinc-700 mb-1.5 block">Email</Label>
          <Input
            type="email"
            value={form.email}
            onChange={(e) => setField('email', e.target.value)}
            placeholder="jane@example.com"
            data-testid="input-email"
            className="h-11"
          />
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label className="text-sm font-semibold text-zinc-700 mb-1.5 block">Phone</Label>
          <Input
            value={form.phone}
            onChange={(e) => setField('phone', e.target.value)}
            placeholder="(503) 555-0100"
            data-testid="input-phone"
            className="h-11"
          />
        </div>
        <div>
          <Label className="text-sm font-semibold text-zinc-700 mb-1.5 block">ZIP Code</Label>
          <div className="relative">
            <Input
              value={form.zipCode}
              onChange={(e) => setField('zipCode', e.target.value)}
              placeholder="97201"
              maxLength={5}
              data-testid="input-zip"
              className="h-11"
            />
            {validating && (
              <Loader2 className="absolute right-3 top-3 h-4 w-4 animate-spin text-zinc-400" />
            )}
            {isServiceable === true && (
              <CheckCircle2 className="absolute right-3 top-3 h-4 w-4 text-green-500" />
            )}
            {isServiceable === false && (
              <AlertCircle className="absolute right-3 top-3 h-4 w-4 text-red-500" />
            )}
          </div>
          {isServiceable === true && serviceAreaName && (
            <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
              <MapPin className="h-3 w-3" /> {serviceAreaName}
            </p>
          )}
          {isServiceable === false && (
            <div className="mt-2">
              <p className="text-xs text-red-600 mb-1">We don&apos;t service this area yet.</p>
              {!showWaitlist ? (
                <button onClick={() => setShowWaitlist(true)} className="text-xs text-[#F56F05] underline">
                  Join waitlist
                </button>
              ) : (
                <WaitlistForm zipCode={form.zipCode} onSuccess={() => setShowWaitlist(false)} />
              )}
            </div>
          )}
        </div>
      </div>

      <div>
        <Label className="text-sm font-semibold text-zinc-700 mb-1.5 block">Service Address</Label>
        <Input
          value={form.serviceAddress}
          onChange={(e) => setField('serviceAddress', e.target.value)}
          placeholder="123 NW 23rd Ave, Portland, OR"
          data-testid="input-address"
          className="h-11"
        />
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label className="text-sm font-semibold text-zinc-700 mb-1.5 block">
            Parking Instructions
          </Label>
          <Textarea
            value={form.parkingInstructions}
            onChange={(e) => setField('parkingInstructions', e.target.value)}
            placeholder="Where to park, gate codes..."
            rows={2}
            data-testid="input-parking"
          />
        </div>
        <div>
          <Label className="text-sm font-semibold text-zinc-700 mb-1.5 block">
            Access Instructions
          </Label>
          <Textarea
            value={form.accessInstructions}
            onChange={(e) => setField('accessInstructions', e.target.value)}
            placeholder="Lockbox code, key location..."
            rows={2}
            data-testid="input-access"
          />
        </div>
      </div>

      <div className="flex items-center gap-3 pt-1">
        <Checkbox
          id="pets"
          checked={form.hasPets}
          onCheckedChange={(v) => setField('hasPets', v)}
          data-testid="checkbox-pets"
        />
        <Label htmlFor="pets" className="text-sm text-zinc-700 cursor-pointer">
          I have pets at home
        </Label>
      </div>
      {form.hasPets && (
        <Input
          value={form.petNotes}
          onChange={(e) => setField('petNotes', e.target.value)}
          placeholder="Type of pets, any notes..."
          data-testid="input-pet-notes"
          className="h-11"
        />
      )}

      <div
        className="mt-3 p-4 bg-zinc-50 rounded-xl border border-zinc-200 grid grid-cols-3 gap-3"
        data-testid="checkout-trust"
      >
        {[
          { icon: Lock, text: 'Secure payment' },
          { icon: Shield, text: 'Background-checked' },
          { icon: Award, text: 'Satisfaction guarantee' },
        ].map((t) => (
          <div key={t.text} className="flex flex-col items-center text-center gap-1.5">
            <t.icon className="h-5 w-5 text-[#F56F05]" />
            <span className="text-xs text-zinc-500 leading-tight">{t.text}</span>
          </div>
        ))}
      </div>
    </div>
  </motion.div>
);
